import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { api, STALE_TIMES } from '@/lib/api'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { updateAppSessionState } from '@/state/session/context'
import { invalidateNotificationsDomain } from '@/lib/query/shell-cache'

const DEBOUNCE_MS = STALE_TIMES.NORMAL // 30 seconds — matches notification stale time

/**
 * Hook that fetches pending friend requests count + refreshes notification
 * summary on every route change, debounced to at most once per 30 seconds.
 *
 * Must be rendered inside BrowserRouter + AuthGuard (e.g. inside AppShell).
 */
export function useRouteChangeNotifications(): void {
  const pathname = usePathname()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const lastFetchRef = useRef<number>(0)
  const userId = user?.id ?? null

  useEffect(() => {
    if (!userId) return

    const now = Date.now()
    const elapsed = now - lastFetchRef.current

    if (elapsed < DEBOUNCE_MS) return

    lastFetchRef.current = now

    // Fire both fetches in parallel — non-blocking
    const fetchPendingCount = async () => {
      try {
        const { count } = await api.get<{ count: number }>(
          '/social/friends/requests/count',
        )

        updateAppSessionState((prev) => {
          const currentCount = prev.navigationBadges?.friends ?? 0
          if (currentCount === count) return prev
          return {
            ...prev,
            navigationBadges: {
              ...prev.navigationBadges,
              friends: count,
            },
          }
        })
      } catch {
        // Silently fail — this is a background enhancement, not critical
      }
    }

    const refreshNotifications = async () => {
      try {
        await invalidateNotificationsDomain(queryClient, userId)
      } catch {
        // Silently fail
      }
    }

    void Promise.all([fetchPendingCount(), refreshNotifications()])
  }, [pathname, userId, queryClient])
}
